"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ImageTag from "../components/reuseable/imageTag";
import { UserLogoutAction } from "../Store/Actions/AuthActions";
import {
  ChevronLeft,
  ChevronRight,
  X,
  LogOut,
  BarChart3,
  Boxes,
  Search,
  MessagesSquare,
  UsersRound,
} from "lucide-react";

// Each item can declare:
//   showForRoles      — only shown for these roles
//   hideForRoles      — hidden for these roles
//   requiresPermission — assistants must have this permission key; non-assistants always see it
const ALL_MENU_ITEMS = [
  {
    title: "Dashboard",
    icon: "/assets/images/icons/home.svg",
    activeIcon: "/assets/images/icons/home-active.svg",
    paths: ["/dashboard"],
  },
  {
    title: "Impact Dashboard",
    Icon: BarChart3,
    paths: ["/dashboard/impact-dashboard"],
  },
  {
    title: "Manage Events",
    icon: "/assets/images/icons/manage-event.svg",
    activeIcon: "/assets/images/icons/manage-event-active.svg",
    paths: ["/dashboard/manage-events", "/dashboard/manage-event-detail"],
    requiresPermission: "events",
  },
  {
    title: "Manage Resources",
    Icon: Boxes,
    paths: ["/dashboard/manage-resources"],
    requiresPermission: "resources",
  },
  {
    title: "Collaboration Workspace",
    icon: "/assets/images/icons/workspace.svg",
    activeIcon: "/assets/images/icons/workspace-active.svg",
    paths: ["/dashboard/collaboration-workspace"],
    requiresPermission: "collaboration",
  },
  {
    title: "Explore Nonprofits",
    Icon: Search,
    paths: ["/dashboard/explore-nonprofit", "/dashboard/collaborate"],
    requiresPermission: "exploreNonprofits",
  },
  {
    title: "Admin Inquiries",
    Icon: MessagesSquare,
    paths: ["/dashboard/admin-inquiries"],
    requiresPermission: "inquiries",
  },
  {
    title: "Personal Space",
    icon: "/assets/images/icons/manage-event.svg",
    activeIcon: "/assets/images/icons/manage-event-active.svg",
    paths: ["/dashboard/personal-space"],
    requiresPermission: "personalSpace",
  },
  {
    title: "Team Access",
    Icon: UsersRound,
    paths: ["/dashboard/assistants"],
    showForRoles: ["non-profit"],
  },
  {
    title: "User Settings",
    icon: "/assets/images/icons/setting.svg",
    activeIcon: "/assets/images/icons/setting-active.svg",
    paths: ["/dashboard/user-setting"],
    hideForRoles: ["assistant"],
  },
];

const Sidebar = ({ isMobileMenuOpen, setIsMobileMenuOpen, isCollapse }) => {
  const [isOpen, setIsOpen] = useState(true);
  const loginSuccess = useSelector((state) => state.auth?.loginSuccess);
  const roleName = loginSuccess?.data?.roleName;
  const assistantPermissions = loginSuccess?.data?.assistantPermissions;
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      dispatch(UserLogoutAction());
      sessionStorage.removeItem("announcementShown"); // reset so popup shows on next login
      navigate("/");
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  // ✅ Corrected Active State Logic
  const isActive = (paths) => {
  return paths.some((path) => {
    if (path === "/dashboard/manage-event-detail" || path === "/dashboard/manage-resources") {
      return location.pathname.startsWith(path);
    }
    // for other paths, exact match
    return location.pathname === path;
  });
};


  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    isCollapse(isOpen);
  };

  const sidebarVariants = {
    open: { width: "24%" },
    closed: { width: "80px", minWidth: "80px" },
  };

  const menuItemVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -10 },
  };

  const mobileSidebarVariants = {
    open: { y: 0, opacity: 1 },
    closed: { y: "-15%", opacity: 0 },
  };

  return (
    <AnimatePresence>
      {(isLargeScreen || isMobileMenuOpen) && (
        <motion.aside
          className={`fixed top-0 left-0 z-40 h-screen w-full bg-[#FDFDFD] shadow-md flex flex-col ${
            isLargeScreen ? "" : "w-full"
          }`}
          initial={isLargeScreen ? "open" : "closed"}
          animate={isLargeScreen ? (isOpen ? "open" : "closed") : "open"}
          exit="closed"
          variants={isLargeScreen ? sidebarVariants : mobileSidebarVariants}
        >
          {/* Header */}
          <div
            className={`flex items-center bg-darkTheme flex-shrink-0 ${
              isOpen ? "justify-between" : "justify-center py-4"
            } px-2 border-b border-[#FFFFFF1A]`}
          >
            {isOpen && (
              <motion.div
                variants={menuItemVariants}
                className="w-full flex justify-center"
              >
                <ImageTag
                  path="/assets/logo.png"
                  classes="w-full h-[107px]"
                  altText="login"
                />
              </motion.div>
            )}
            {isLargeScreen && (
              <button
                onClick={toggleSidebar}
                className={`p-1 rounded-full bg-themeColor text-white ${
                  isOpen ? "xl:-mt-8 2xl:-mt-12" : ""
                }`}
              >
                {isOpen ? <ChevronLeft /> : <ChevronRight />}
              </button>
            )}
            {!isLargeScreen && (
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-full text-white"
              >
                <X />
              </button>
            )}
          </div>

          {/* Nav — scrollable, takes remaining space */}
          <nav className="flex-1 overflow-y-auto space-y-2 mt-2 xl:mt-3 2xl:mt-4 pb-2">
            {ALL_MENU_ITEMS.filter((item) => {
              if (item.showForRoles && !item.showForRoles.includes(roleName)) return false;
              if (item.hideForRoles && item.hideForRoles.includes(roleName)) return false;
              if (item.requiresPermission && roleName === "assistant") {
                return assistantPermissions?.[item.requiresPermission] === true;
              }
              return true;
            }).map((item) => (
              <div key={item.title} className="lg:w-[90%] mx-auto">
                <Link
                  to={item.paths[0]}
                  className={`
                    group relative flex items-center font-popinsSemiBold ${!isOpen ? 'px-4' : 'px-6'} py-2 2xl:py-2.5 rounded-lg
                    transition-all duration-300 ease-in-out
                    ${
                      isActive(item.paths)
                        ? "bg-themeColor !text-white"
                        : `hover:bg-themeColor hover:text-white`
                    }  `}
                  onClick={() => !isLargeScreen && setIsMobileMenuOpen(false)}
                >
                  {/* Gradient Bar (simulating ::before) */}
                  <span
                    className={`absolute right-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-darkTheme transition-all duration-300 delay-75 ease-in-out  ${
                      isActive(item.paths)
                        ? "h-[70%]"
                        : "h-0 group-hover:h-[70%]"
                    }`}
                  ></span>

                  {/* Icon */}
                  <div className="relative">
                    <div
                      className={`xl:p-1.5 2xl:p-2 rounded-lg
${isActive(item.paths) ? "bg-white" : "bg-themeColor"} group-hover:bg-white
`}
                    >
                      {item.Icon ? (
                        <item.Icon
                          className={`xl:size-4 2xl:size-5 transition-all duration-300 ease-in-out group-hover:text-themeColor ${
                            isActive(item.paths) ? "text-themeColor" : "text-white"
                          }`}
                        />
                      ) : (
                        <>
                          <img
                            src={isActive(item.paths) ? item.activeIcon : item.icon}
                            className={`xl:size-4 2xl:size-5 transition-all duration-300 ease-in-out group-hover:hidden`}
                            alt=""
                          />
                          <img
                            src={item.activeIcon}
                            className={`xl:size-4 2xl:size-5 hidden group-hover:block transition-all duration-300 ease-in-out`}
                            alt=""
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Label */}
                  {(isOpen || !isLargeScreen) && (
                    <motion.span variants={menuItemVariants} className="pl-3 text-sm 2xl:text-base whitespace-nowrap">
                      {item.title}
                    </motion.span>
                  )}
                </Link>
              </div>
            ))}
          </nav>

          {/* Logout — always pinned at bottom */}
          <div className="py-2 flex-shrink-0 w-[90%] mx-auto border-t border-gray-100">
            <div
              className={`group relative flex items-center font-popinsSemiBold px-2 ${!isOpen ? 'lg:px-4' : 'lg:px-6'} py-2.5
    transition-all duration-300 ease-in-out hover:bg-themeColor hover:text-white cursor-pointer rounded-lg`}
              onClick={handleLogout}
            >
              <span
                className={`absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-r-md bg-darkTheme transition-all duration-300 ease-in-out
                 h-0 group-hover:h-[70%]
                `}
              ></span>
              <div className="bg-themeColor p-2 rounded-lg text-white group-hover:bg-white group-hover:text-themeColor">
                <LogOut className="size-4 2xl:size-5" />
              </div>
              {(isOpen || !isLargeScreen) && (
                <motion.span
                  variants={menuItemVariants}
                  className="pl-3 text-sm 2xl:text-base"
                >
                  Log Out
                </motion.span>
              )}
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
